import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

import { getOffset } from "Shared/Utils/shared";
import { LIMIT_PAGE } from "Shared/Constants/app";
import { AssignmentList } from "Shared/Types/fleetmanagementapi";
import { selectDashboardVehicleAssignmentList } from "Redux/Dashboard/slice";
import {
  firstDateOfCurrentMonth,
  firstDateOfCurrentYear,
  lastDateOfCurrentMonth,
  lastDateOfCurrentYear,
} from "Shared/Constants/dashboard";

type GroupedAssignment = {
  vehicle_name: string;
  data: AssignmentList[];
};

const useDashboardVehicleAssignments = () => {
  const [searchParams] = useSearchParams();
  const vehicleAssignmentsList = useSelector(
    selectDashboardVehicleAssignmentList
  );

  const onDateRangeChange = (searchParams: URLSearchParams) => {
    searchParams.delete("year");
    searchParams.delete("calendar_type");
  };

  const onCalendarTypeChange = (
    searchParams: URLSearchParams,
    value: string
  ) => {
    if (value === "year") {
      searchParams.set("assignments_from", firstDateOfCurrentYear);
      searchParams.set("assignments_to", lastDateOfCurrentYear);
      searchParams.set("year", currentYear.toString());
    } else {
      searchParams.delete("year");
      searchParams.set("assignments_from", firstDateOfCurrentMonth);
      searchParams.set("assignments_to", lastDateOfCurrentMonth);
    }
    searchParams.delete("date_range");
  };

  const currentYear = dayjs().year();

  const params = useMemo(
    () => ({
      offset: getOffset(+(searchParams.get("page") || 1)),
      limit: LIMIT_PAGE,
      vehicle: searchParams.get("assignment_vehicle"),
      date_range_after: searchParams.get("from"),
      date_range_before: searchParams.get("to"),
      year: searchParams.get("year"),
    }),
    [searchParams]
  );

  const groupedDataByVehicle = useMemo(() => {
    return (
      vehicleAssignmentsList &&
      vehicleAssignmentsList.reduce(
        (acc: GroupedAssignment[], currentItem: AssignmentList) => {
          const vehicleName = currentItem.vehicle?.name;

          if (!vehicleName) return acc;

          const existingGroup = acc.find(
            (group) => group.vehicle_name === vehicleName
          );

          if (existingGroup) {
            existingGroup.data.push(currentItem);
          } else {
            acc.push({
              vehicle_name: vehicleName,
              data: [currentItem],
            });
          }

          return acc;
        },
        [] as GroupedAssignment[]
      )
    );
  }, [vehicleAssignmentsList]);

  return {
    params,
    currentYear,
    groupedDataByVehicle,
    onCalendarTypeChange,
    onDateRangeChange,
  };
};

export default useDashboardVehicleAssignments;
